import React, { createContext, useCallback, useContext, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, IconButton, Box, Typography, Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface GlobalError {
    message: string;
    details?: string;
}

interface GlobalErrorContextType {
    showError: (message: string, details?: string) => void;
}

const GlobalErrorContext = createContext<GlobalErrorContextType>({
    showError: () => {}
});

export const useGlobalError = () => useContext(GlobalErrorContext);

let globalShowError: (message: string, details?: string) => void = () => {};

/** Call this from non-React code (e.g. ApiService) to show a global error dialog. */
export const showGlobalError = (message: string, details?: string) => {
    globalShowError(message, details);
};

export const GlobalErrorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [error, setError] = useState<GlobalError | null>(null);
    const [showDetails, setShowDetails] = useState(false);

    const showError = useCallback((message: string, details?: string) => {
        setError({ message, details });
        setShowDetails(false);
    }, []);

    // Keep the imperative reference in sync
    globalShowError = showError;

    const handleClose = () => {
        setError(null);
    };

    return (
        <GlobalErrorContext.Provider value={{ showError }}>
            {children}
            <Dialog
                open={error !== null}
                onClose={handleClose}
            >
                <DialogTitle id="alert-dialog-title" color="primary" variant="h5">
                    Error
                </DialogTitle>
                <DialogContent>
                    <Box minWidth={300}>
                        <Typography marginBottom={1}>
                            {error?.message}
                        </Typography>
                        {error?.details && (
                            <Box>
                                <Box
                                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                                    onClick={() => setShowDetails(!showDetails)}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        Technical Details
                                    </Typography>
                                    <IconButton size="small">
                                        <ExpandMoreIcon
                                            sx={{
                                                transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s'
                                            }}
                                        />
                                    </IconButton>
                                </Box>
                                <Collapse in={showDetails}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 1, p: 1,
                                            bgcolor: 'grey.100',
                                            borderRadius: 1,
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        {error?.details}
                                    </Typography>
                                </Collapse>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Box flexGrow={1} />
                    <Button size="small" onClick={handleClose} color="secondary" variant="outlined" sx={{ mb: 1.5, mr: 2 }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </GlobalErrorContext.Provider>
    );
};
